import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { REGISTRATION_TEXT } from './lang';
import { WelcomeLetter } from './WelcomeLetter';
import { CreateStudentRecord } from './CreateStudentRecord';
import { CreateStudentRecordUnavailable } from './CreateStudentRecordUnavailable';
import { StudentCardBooking } from './StudentCardBooking';
import {
  OVTS_COMPLETION_STATUS,
  useGetRegistrationData,
} from '../../../graphql';
import { Stepper, type Steps, Alert, Loading } from '../../../../../components';
import { MandatoryTraining } from './MandatoryTraining';

type OnlineRegistrationProps = {
  isLocal: boolean;
  studentNumber: string;
  entryYear: string;
  refetchApplicationNum: () => void;
};

export const OnlineRegistration: React.FC<OnlineRegistrationProps> = (
  props: OnlineRegistrationProps,
) => {
  const { isLocal, studentNumber, entryYear, refetchApplicationNum } = props;
  const { applicationId = '' } = useParams();
  const {
    studentSelectedTime,
    checkOVTSCompletion,
    loading,
    error,
    refetch: refetchRegistrationData,
  } = useGetRegistrationData(applicationId);

  const completeOnlineTraining =
    checkOVTSCompletion === OVTS_COMPLETION_STATUS.COMPLETED ||
    checkOVTSCompletion === OVTS_COMPLETION_STATUS.EXEMPTED;
  const { slotId } = studentSelectedTime;

  const steps: Steps[] = [
    {
      label: REGISTRATION_TEXT.STEPS.PERSONAL_PARTICULARS,
      hasDone: Boolean(studentNumber),
      clickableStep: true,
    },
    {
      label: REGISTRATION_TEXT.STEPS.WELCOME_LETTER,
      clickableStep: Boolean(studentNumber),
      hasDone: Boolean(studentNumber),
    },
    {
      label: REGISTRATION_TEXT.STEPS.ONLINE_TRAINING,
      clickableStep: Boolean(studentNumber),
      hasDone: Boolean(studentNumber) && completeOnlineTraining,
    },
    {
      label: REGISTRATION_TEXT.STEPS.STUDENT_CARD_BOOKING,
      clickableStep: Boolean(studentNumber) && completeOnlineTraining,
      hasDone: Boolean(slotId),
    },
  ];

  const [activeStep, setActiveStep] = useState(0);

  const handleJumpNext = (): void => {
    setActiveStep(1);
  };

  const isRegistrationOpen = process.env.REACT_APP_REGISTRATION_ON === 'true';
  const componentsMap: React.ReactNode[] = [
    // Switch back to <CreateStudentRecord> when Online Registration is ready.
    // props are kept but not used for minimum migration cost.
    // 20/11/2024 open registration in dev
    isRegistrationOpen ? (
      <CreateStudentRecord
        key={1}
        refetchApplicationNum={refetchApplicationNum}
        handleJumpNext={handleJumpNext}
      />
    ) : (
      <CreateStudentRecordUnavailable
        key={1}
        refetchApplicationNum={refetchApplicationNum}
        handleJumpNext={handleJumpNext}
      />
    ),

    <WelcomeLetter key={2} isLocal={isLocal} studentNumber={studentNumber} />,

    <MandatoryTraining
      key={3}
      onlineTrainingStatus={checkOVTSCompletion as OVTS_COMPLETION_STATUS}
    />,

    <StudentCardBooking
      key={4}
      studentNumber={studentNumber}
      entryYear={entryYear}
      studentSelectedTime={studentSelectedTime}
      refetchCollectTime={refetchRegistrationData}
    />,
  ];

  useEffect(() => {
    // reset active step in the first undone step
    if (!loading) {
      const undoneStepIndex = steps.findIndex(step => !step?.hasDone);
      const nextActiveStep =
        undoneStepIndex === -1 ? steps.length - 1 : undoneStepIndex;
      setActiveStep(nextActiveStep);
    }
  }, [loading]);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return loading ? (
    <Loading />
  ) : (
    <Container>
      <div className="flex flex-row pl-2 pr-4 w-full">
        <div className="flex w-4/5">
          <div className="content-container pr-6 w-full">
            {componentsMap[activeStep]}
          </div>
        </div>
        <div className="w-1/5">
          <Stepper
            width={200}
            activeStep={activeStep}
            hideButton={steps[activeStep].hasDone === false}
            onChange={index => {
              setActiveStep(index);
            }}
            steps={steps}
          />
        </div>
      </div>
    </Container>
  );
};

export const Container = styled.div`
  width: 100%;

  .content-container {
    min-height: 450px;
  }
`;
